(function () {
  if (typeof customElements === 'undefined') return;
  if (customElements.get('commercial-space-carousel')) return;

  class CommercialSpaceCarousel extends HTMLElement {
    constructor() {
      super();
      this.bgSwiper = null;
      this.contentSwiper = null;
      this._resizeHandler = null;
      this._resizeRaf = 0;
      this._imgLoadHandlers = [];
    }

    connectedCallback() {
      this.initWhenReady();
    }

    initWhenReady() {
      if (typeof Swiper !== 'undefined') {
        this.initSwipers();
      } else {
        document.addEventListener('DOMContentLoaded', () => this.waitForSwiper());
      }
    }

    waitForSwiper() {
      if (typeof Swiper !== 'undefined') {
        this.initSwipers();
      } else {
        setTimeout(() => this.waitForSwiper(), 50);
      }
    }

    initSwipers() {
      const slideCount = parseInt(this.dataset.slideCount, 10) || 0;
      if (slideCount === 0) return;

      const bgEl = this.querySelector('.commercial-space-carousel__swiper-bg');
      const contentEl = this.querySelector('.commercial-space-carousel__swiper-content');
      if (!bgEl || !contentEl) return;

      if (this.bgSwiper || this.contentSwiper) return;

      const autoplay = this.dataset.autoplay === 'true';
      const autoplayDelay = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
      const bgEffect = this.dataset.bgEffect || 'fade';
      const contentEffect = this.dataset.contentEffect || 'slide';
      const loop = slideCount > 1;
      const speed = 600;

      const bgConfig = {
        loop: loop,
        effect: bgEffect,
        speed: speed,
        allowTouchMove: loop,
        grabCursor: false,
        watchSlidesProgress: true,
      };
      if (bgEffect === 'fade') {
        bgConfig.fadeEffect = { crossFade: true };
      }

      const contentConfig = {
        loop: loop,
        effect: contentEffect,
        speed: speed,
        allowTouchMove: loop,
        grabCursor: loop,
        watchSlidesProgress: true,
      };
      if (contentEffect === 'fade') {
        contentConfig.fadeEffect = { crossFade: true };
      }

      if (slideCount > 1) {
        const paginationEl = this.querySelector('.commercial-space-carousel__pagination');
        if (paginationEl) {
          contentConfig.pagination = {
            el: paginationEl,
            clickable: true,
            type: 'bullets',
          };
        }
      }

      if (autoplay && loop) {
        contentConfig.autoplay = {
          delay: autoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        };
      }

      this.bgSwiper = new Swiper(bgEl, bgConfig);
      this.contentSwiper = new Swiper(contentEl, contentConfig);

      this.contentSwiper.controller.control = this.bgSwiper;
      this.bgSwiper.controller.control = this.contentSwiper;

      this.setupMaxHeightSync();
      this.initEditorEvents();
    }

    setupMaxHeightSync() {
      const schedule = () => {
        requestAnimationFrame(() => this.syncMaxHeight());
      };

      schedule();

      this._resizeHandler = () => {
        if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
        this._resizeRaf = requestAnimationFrame(() => {
          this._resizeRaf = 0;
          this.syncMaxHeight();
        });
      };
      window.addEventListener('resize', this._resizeHandler);

      if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
        document.fonts.ready.then(() => schedule()).catch(() => {});
      }

      const imgs = this.querySelectorAll('.commercial-space-carousel__card img');
      imgs.forEach((img) => {
        if (img.complete) return;
        const handler = () => schedule();
        img.addEventListener('load', handler);
        img.addEventListener('error', handler);
        this._imgLoadHandlers.push({ img, handler });
      });
    }

    syncMaxHeight() {
      const slides = this.querySelectorAll('.commercial-space-carousel__content-slide');
      if (!slides.length) return;

      let max = 0;
      slides.forEach((slide) => {
        const prevHeight = slide.style.height;
        const prevMinHeight = slide.style.minHeight;
        slide.style.height = 'auto';
        slide.style.minHeight = '0';
        const h = slide.scrollHeight;
        if (h > max) max = h;
        slide.style.height = prevHeight;
        slide.style.minHeight = prevMinHeight;
      });

      if (max > 0) {
        this.style.setProperty('--csc-card-min-height', max + 'px');
      }
    }

    initEditorEvents() {
      if (!window.Shopify || !Shopify.designMode) return;
      const sectionId = this.dataset.sectionId;

      document.addEventListener('shopify:section:load', (e) => {
        if (e.detail.sectionId !== sectionId) return;
        this.destroySwipers();
        this.initWhenReady();
      });
    }

    destroySwipers() {
      if (this._resizeHandler) {
        window.removeEventListener('resize', this._resizeHandler);
        this._resizeHandler = null;
      }
      if (this._resizeRaf) {
        cancelAnimationFrame(this._resizeRaf);
        this._resizeRaf = 0;
      }
      if (this._imgLoadHandlers && this._imgLoadHandlers.length) {
        this._imgLoadHandlers.forEach(({ img, handler }) => {
          img.removeEventListener('load', handler);
          img.removeEventListener('error', handler);
        });
        this._imgLoadHandlers = [];
      }
      this.style.removeProperty('--csc-card-min-height');

      if (this.bgSwiper && this.bgSwiper.controller) {
        this.bgSwiper.controller.control = undefined;
      }
      if (this.contentSwiper && this.contentSwiper.controller) {
        this.contentSwiper.controller.control = undefined;
      }
      if (this.bgSwiper) {
        this.bgSwiper.destroy(true, true);
        this.bgSwiper = null;
      }
      if (this.contentSwiper) {
        this.contentSwiper.destroy(true, true);
        this.contentSwiper = null;
      }
    }

    disconnectedCallback() {
      this.destroySwipers();
    }
  }

  customElements.define('commercial-space-carousel', CommercialSpaceCarousel);
})();
